import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { useShare } from './useShare';
import { VideoContent } from '../types/content';
import { getShareableUrl } from '../utils/url';

interface UseVideoActionsProps {
  video?: VideoContent;
  onSaveSuccess?: () => void;
  onSaveError?: (error: Error) => void;
  onShareSuccess?: () => void;
  onShareError?: (error: Error) => void;
  onDownloadSuccess?: () => void;
  onDownloadError?: (error: Error) => void;
}

export const useVideoActions = ({
  video,
  onSaveSuccess,
  onSaveError,
  onShareSuccess,
  onShareError,
  onDownloadSuccess,
  onDownloadError,
}: UseVideoActionsProps) => {
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);

  const { shareOptions, handleQuickShare, handleCopyLink } = useShare({
    url: getShareableUrl(window.location.pathname),
    title: video?.title,
    text: video?.description,
  });

  const handleShare = async () => {
    try {
      if (!video) return;

      if (Capacitor.isNativePlatform()) {
        // Use Capacitor Share plugin for mobile
        await Share.share({
          title: video.title,
          text: video.description,
          url: getShareableUrl(window.location.pathname),
          dialogTitle: t('common.share'),
        });
      } else if (typeof navigator !== 'undefined' && 'share' in navigator) {
        await handleQuickShare();
      } else {
        setIsSharePopupOpen(true);
      }
      onShareSuccess?.();
    } catch (error) {
      onShareError?.(error as Error);
    }
  };

  const handleSave = async () => {
    try {
      // TODO: Implement actual save functionality when backend is ready
      setIsSaved(!isSaved);
      onSaveSuccess?.();
    } catch (error) {
      onSaveError?.(error as Error);
    }
  };

  const handleDownload = async () => {
    try {
      // TODO: Implement download functionality when backend is ready
      onDownloadSuccess?.();
    } catch (error) {
      onDownloadError?.(error as Error);
    }
  };

  const closeSharePopup = () => {
    setIsSharePopupOpen(false);
  };

  return {
    isSaved,
    isSharePopupOpen,
    showShareTooltip,
    shareOptions,
    handleShare,
    handleSave,
    handleDownload,
    closeSharePopup,
    setShowShareTooltip,
  };
}; 